import { reactive, computed } from 'vue'
import { authApi, authVisitorApi } from '../api'

// 用户状态管理
const userState = reactive({
  user: null,
  token: null,
  isAuthenticated: false,
  isGuest: false,
  isLoading: false
})

// 用户操作
export const useAuth = () => {
  // 初始化用户状态
  const initAuth = async () => {
    const token = localStorage.getItem('auth_token')
    const isGuestMode = localStorage.getItem('guest_mode') === 'true'
    const userInfo = localStorage.getItem('user_info')
    
    if (isGuestMode) {
      try {
        const resp = await authVisitorApi.getVisitor()
        const visitorUser = resp.data?.user || {
          id: null,
          username: 'visitor',
          role: 'visitor',
          roles: ['visitor'],
          permissions: []
        }
        userState.user = visitorUser
        userState.token = null
        userState.isAuthenticated = true
        userState.isGuest = true
        localStorage.setItem('user_info', JSON.stringify(visitorUser))
        return
      } catch (e) {
        console.error('加载访客权限失败:', e)
        // fallback to cached info
        const fallbackGuest = userInfo ? JSON.parse(userInfo) : {
          id: null,
          username: 'visitor',
          role: 'visitor',
          roles: ['visitor'],
          permissions: []
        }
        userState.user = fallbackGuest
        userState.token = null
        userState.isAuthenticated = true
        userState.isGuest = true
        return
      }
    }

    if (token && userInfo) {
      userState.token = token
      userState.user = JSON.parse(userInfo)
      userState.isAuthenticated = true
      userState.isGuest = false

      // 拉取最新权限信息，避免本地缓存过期
      try {
        const resp = await authApi.verifyToken()
        const freshUser = resp.data?.user || userState.user
        userState.user = freshUser
        localStorage.setItem('user_info', JSON.stringify(freshUser))
      } catch (e) {
        // 验证失败则登出
        logout()
      }
    }
  }

  // 登录
  const login = async (credentials) => {
    try {
      userState.isLoading = true
      const response = await authApi.login(credentials)
      
      const { user, token } = response.data
      // 保存到本地存储
      localStorage.setItem('auth_token', token)
      localStorage.setItem('user_info', JSON.stringify(user))
      localStorage.removeItem('guest_mode')
      
      // 更新状态
      userState.user = user
      userState.token = token
      userState.isAuthenticated = true
      userState.isGuest = false
      
      return { success: true, user }
    } catch (error) {
      console.error('登录失败:', error)
      return { success: false, error: error.message }
    } finally {
      userState.isLoading = false
    }
  }

  // 注册
  const register = async (userData) => {
    try {
      userState.isLoading = true
      const response = await authApi.register(userData)
      
      const { user, token } = response.data
      // 保存到本地存储
      localStorage.setItem('auth_token', token)
      localStorage.setItem('user_info', JSON.stringify(user))
      localStorage.removeItem('guest_mode')
      
      // 更新状态
      userState.user = user
      userState.token = token
      userState.isAuthenticated = true
      userState.isGuest = false
      
      return { success: true, user }
    } catch (error) {
      console.error('注册失败:', error)
      return { success: false, error: error.message }
    } finally {
      userState.isLoading = false
    }
  }

  // 登出
  const logout = () => {
    // 清除本地存储
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user_info')
    localStorage.removeItem('guest_mode')
    
    // 重置状态
    userState.user = null
    userState.token = null
    userState.isAuthenticated = false
    userState.isGuest = false
    
    // 重定向到登录页（使用replace避免在历史记录中留下当前页面）
    if (window.location.pathname !== '/login') {
      window.location.replace('/login')
    }
  }

  // 更新用户信息
  const updateProfile = async (userData) => {
    try {
      if (userState.isGuest) {
        console.warn('访客模式不支持更新个人信息')
        return { success: false, error: '访客模式不支持更新信息' }
      }
      userState.isLoading = true
      const response = await authApi.updateProfile(userData)
      
      const { user } = response.data
      
      // 更新本地存储
      localStorage.setItem('user_info', JSON.stringify(user))
      
      // 更新状态
      userState.user = user
      
      return { success: true, user }
    } catch (error) {
      console.error('更新用户信息失败:', error)
      return { success: false, error: error.message }
    } finally {
      userState.isLoading = false
    }
  }

  // 修改密码
  const changePassword = async (passwordData) => {
    try {
      if (userState.isGuest) {
        console.warn('访客模式不支持修改密码')
        return { success: false, error: '访客模式不支持修改密码' }
      }
      userState.isLoading = true
      await authApi.changePassword(passwordData)
      
      return { success: true }
    } catch (error) {
      console.error('修改密码失败:', error)
      return { success: false, error: error.message }
    } finally {
      userState.isLoading = false
    }
  }

  // 验证令牌
  const verifyToken = async () => {
    try {
      if (userState.isGuest) {
        return { success: true, user: userState.user }
      }
      const response = await authApi.verifyToken()
      const { user } = response.data
      
      // 更新用户信息
      userState.user = user
      localStorage.setItem('user_info', JSON.stringify(user))
      
      return { success: true, user }
    } catch (error) {
      console.error('令牌验证失败:', error)
      // 使用logout函数统一处理登出逻辑
      logout()
      return { success: false, error: error.message }
    }
  }

  // 检查是否为管理员
  const isAdmin = computed(() => {
    return userState.user && userState.user.role === 'admin'
  })
  const userPermSet = computed(() => {
    const raw = userState.user?.permissions || []
    const keys = raw
      .map(p => {
        if (typeof p === 'string') return p
        if (p && typeof p === 'object' && p.key) return p.key
        return null
      })
      .filter(Boolean)
    return new Set(keys)
  })
  const hasPermission = (required = []) => {
    if (!required || required.length === 0) return true
    if (!userState.user) return false
    const permSet = userPermSet.value
    if (permSet.has('*')) return true
    const groups = Array.isArray(required[0]) ? required : [required]
    return groups.some(group => group.every(p => permSet.has(p)))
  }

  const isGuest = computed(() => userState.isGuest)

  // 检查是否已登录
  const isLoggedIn = computed(() => {
    return userState.isAuthenticated && userState.user
  })

  return {
    // 状态
    user: computed(() => userState.user),
    token: computed(() => userState.token),
    isAuthenticated: computed(() => userState.isAuthenticated),
    isLoading: computed(() => userState.isLoading),
    isAdmin,
    isGuest,
    isLoggedIn,
    hasPermission,
    
    // 方法
    initAuth,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    verifyToken,
    loginAsGuest: async () => {
      try {
        const resp = await authVisitorApi.getVisitor()
        const visitorUser = resp.data?.user || {
          id: null,
          username: 'visitor',
          role: 'visitor',
          roles: ['visitor'],
          permissions: []
        }
        localStorage.removeItem('auth_token')
        localStorage.setItem('user_info', JSON.stringify(visitorUser))
        localStorage.setItem('guest_mode', 'true')

        userState.user = visitorUser
        userState.token = null
        userState.isAuthenticated = true
        userState.isGuest = true

        return { success: true, user: visitorUser }
      } catch (error) {
        console.error('访客登录失败:', error)
        return { success: false, error: error.message }
      }
    }
  }
}

// 导出状态（用于非组合式API）
export { userState }
